import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AudioButton from "../AudioButton";
import Confetti from "../Confetti";
import { speak, speakPhoneme, preload } from "../../services/phonicsAudio";
import { sfx } from "../../services/soundfx";

export default function FeedingMonsterGame({ level, onFinish }) {
  const rounds = level.rounds;
  const [idx, setIdx] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [firstTry, setFirstTry] = useState(true);
  const [perfectCount, setPerfectCount] = useState(0);
  const [mood, setMood] = useState("hungry"); // hungry | happy | burp
  const [burst, setBurst] = useState(false);
  const [wrongId, setWrongId] = useState(null);
  const round = rounds[idx];

  const promptText = `Which one starts with the ${round.sound} sound?`;

  const playPrompt = useCallback(() => {
    speak(`I want the ${round.sound}... ${round.sound} sound! Which one?`);
  }, [round.sound]);

  useEffect(() => {
    preload([
      { text: round.sound, kind: "phoneme" },
      ...round.options.map((o) => ({ text: o.label, kind: "word" })),
    ]);
    const t = setTimeout(() => {
      speakPhoneme(round.sound);
      setTimeout(playPrompt, 700);
    }, 400);
    return () => clearTimeout(t);
  }, [idx]); // eslint-disable-line

  const handlePick = (opt) => {
    if (mood === "happy") return;
    if (opt.letter === round.sound) {
      sfx.chomp();
      setMood("happy");
      setBurst(true);
      if (firstTry) setPerfectCount((c) => c + 1);
      setCorrectCount((c) => c + 1);
      speak("Yum yum! That is right!");
      setTimeout(() => setBurst(false), 1400);
      setTimeout(() => {
        if (idx + 1 >= rounds.length) {
          finish();
        } else {
          setIdx((i) => i + 1);
          setMood("hungry");
          setFirstTry(true);
        }
      }, 1700);
    } else {
      sfx.burp();
      setMood("burp");
      setFirstTry(false);
      setWrongId(opt.label);
      speak("Oops! Burp! Try again!");
      setTimeout(() => {
        setMood("hungry");
        setWrongId(null);
      }, 900);
    }
  };

  const finish = () => {
    const total = rounds.length;
    const stars = perfectCount === total ? 3 : perfectCount >= total - 1 ? 2 : 1;
    onFinish({ stars, accuracy: correctCount / total });
  };

  return (
    <div className="relative flex flex-col items-center px-4 pt-3 pb-8 min-h-full no-select">
      <Confetti show={burst} count={40} />

      {/* Prompt */}
      <div className="flex items-center gap-3 bg-white/95 rounded-3xl border-4 border-violet-300 shadow-lg px-4 py-3 mt-2 max-w-lg">
        <AudioButton text={`I want the ${round.sound} sound. Which one?`} testId="prompt-audio" size={56} />
        <p className="font-display text-lg sm:text-xl font-bold text-violet-900" data-testid="game-prompt">
          Feed me something that starts with <span className="text-coral font-black" style={{ color: "#FF6B6B" }}>/{round.sound}/</span>!
        </p>
      </div>

      {/* Monster */}
      <motion.div
        animate={mood === "happy" ? { scale: [1, 1.15, 1], rotate: [0, -5, 5, 0] } : mood === "burp" ? { x: [0, -8, 8, -6, 0] } : { y: [0, -8, 0] }}
        transition={mood === "hungry" ? { repeat: Infinity, duration: 2 } : { duration: 0.6 }}
        className="relative my-5"
        data-testid="monster"
      >
        <div className="relative" style={{ width: 180, height: 180 }}>
          <div className="absolute inset-0 rounded-[45%] bg-violet-500 shadow-[0_12px_0_#6D28D9]" />
          {/* eyes */}
          <div className="absolute left-9 top-10 w-12 h-12 rounded-full bg-white flex items-center justify-center">
            <div className="w-5 h-5 rounded-full bg-slate-900" />
          </div>
          <div className="absolute right-9 top-10 w-12 h-12 rounded-full bg-white flex items-center justify-center">
            <div className="w-5 h-5 rounded-full bg-slate-900" />
          </div>
          {/* mouth */}
          <motion.div
            animate={{ height: mood === "happy" ? 70 : 42 }}
            className="absolute left-1/2 -translate-x-1/2 bottom-8 w-24 rounded-b-[40px] rounded-t-xl bg-slate-900 overflow-hidden flex items-end justify-center"
          >
            <div className="w-16 h-6 rounded-t-full bg-pink-400 mb-1" />
          </motion.div>
          {/* horns */}
          <div className="absolute -top-3 left-10 w-4 h-8 rounded-full bg-amber-400 rotate-[-20deg]" />
          <div className="absolute -top-3 right-10 w-4 h-8 rounded-full bg-amber-400 rotate-[20deg]" />
        </div>
        {mood === "burp" && (
          <div className="absolute -right-4 top-0 text-3xl animate-wobble">💨</div>
        )}
      </motion.div>

      {/* Food options */}
      <div className="flex flex-wrap items-center justify-center gap-4 mt-2 max-w-xl">
        <AnimatePresence>
          {round.options.map((opt) => (
            <motion.button
              key={opt.label}
              data-testid={`food-option-${opt.letter}`}
              onClick={() => handlePick(opt)}
              initial={{ y: 40, opacity: 0 }}
              animate={{
                y: wrongId === opt.label ? [0, -10, 0] : 0,
                opacity: 1,
                rotate: wrongId === opt.label ? [0, -8, 8, 0] : 0,
              }}
              transition={{ type: "spring", stiffness: 260, damping: 18 }}
              whileTap={{ scale: 0.9 }}
              className="pop-btn flex flex-col items-center justify-center rounded-3xl bg-white border-4 border-amber-200 shadow-[0_6px_0_#FBBF24] active:shadow-none"
              style={{ width: 104, height: 116 }}
            >
              <span className="text-5xl">{opt.emoji}</span>
              {level.showLetters && (
                <span className="font-display text-xl font-black text-blue-600 mt-1">{opt.letter}</span>
              )}
            </motion.button>
          ))}
        </AnimatePresence>
      </div>

      {/* progress dots */}
      <div className="flex gap-2 mt-7">
        {rounds.map((_, i) => (
          <div key={i} className={`w-3 h-3 rounded-full ${i < idx ? "bg-green-500" : i === idx ? "bg-amber-400 animate-pulse" : "bg-slate-300"}`} />
        ))}
      </div>
    </div>
  );
}
